import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import APIs from 'src/api';
import UploadingDialog from 'src/common/dialogs/UploadingDialog';
import { DialogInOne } from 'src/components';
import { Box, Button, Checkbox, DialogActions, Typography } from 'src/components/MuiGenerals';
import { useAlerting, useTranslation } from 'src/hooks';
import { Iprefix } from 'src/hooks/useTranslation';
import { useZusTranslatePrefixStore } from 'src/zustand/store';
import Content from './components/Content';
import ExportResultDialog from './components/ExportResultDialog';
import InvalidRecordReminderDialog from './components/InvalidRecordReminderDialog';
import PreventCancelDialog from './components/PreventCancelDialog';
import { ApiResponse } from './types/ApiResponse';
import { Preview } from './types/Preview';

type Props = {
  originalTranslatePrefix: Iprefix;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  refetchTable: () => void;
};

const BatchAllocateCreditDialog = (props: Props) => {
  const { isOpen, originalTranslatePrefix, setIsOpen, refetchTable } = props;
  const { t, tc } = useTranslation('clientCreditAllocation');
  const { setTranslatePrefix } = useZusTranslatePrefixStore();
  const { alerting } = useAlerting();

  //Dialog Toggle
  const [isInvalidRecordReminderDialogOpened, setIsInvalidRecordReminderDialogOpened] =
    useState(false);
  const [isUploadingDialogOpened, setIsUploadingDialogOpened] = useState(false);
  const [isExportResultDialogOpened, setIsExportResultDialogOpened] = useState(false);
  const [isPreventCancelDialogOpened, setIsPreventCancelDialogOpened] = useState(false);

  //Check box
  const [isCheckBoxClicked, setIsCheckBoxClicked] = useState(false);

  //previews data
  const [previews, setPreviews] = useState<Preview[]>([]);

  //result from batch Allocate credit
  const [apiResponses, setApiResponses] = useState<ApiResponse[]>([]);

  useEffect(() => {
    setIsCheckBoxClicked(false);
  }, [previews]);

  const validPreviews = previews.filter((item) => item.paramData);

  const numberOfValidPreviews = validPreviews.length;

  const isSomeValidPreviews =
    previews.length > 0 && numberOfValidPreviews > 0 && numberOfValidPreviews !== previews.length;

  const onConfirm = async () => {
    if (previews.length === 0) {
      return alerting('error', t('pleaseUploadFile'));
    }

    if (previews.length > 0 && numberOfValidPreviews === 0) {
      return alerting('error', t('allErrors'));
    }

    if (isSomeValidPreviews && !isCheckBoxClicked) {
      return setIsInvalidRecordReminderDialogOpened(true);
    }

    setIsUploadingDialogOpened(true);
    for (let i = 0; i < validPreviews.length; i++) {
      const row = validPreviews[i];
      try {
        const res: ApiResponse = await APIs.clientManagement.creditAllocation.postPaToClient(
          { ...row.paramData, clientName: '-' },
          { isSkipErrorHandling: true }
        );
        if (!res) {
          throw new Error(t('unknownError'));
        }
        setApiResponses((prev) => [...prev, { ...res, originalRowNumber: row.originalRowNumber }]);
      } catch (error: any) {
        const errorMessage = error?.message ?? t('unknownError');
        const errorResponse: ApiResponse = {
          originalRowNumber: row.originalRowNumber,
          error: {
            message: errorMessage,
          },
        };
        setApiResponses((prev) => [...prev, errorResponse]);
      }
    }
    refetchTable();
    setIsUploadingDialogOpened(false);
    setIsExportResultDialogOpened(true);
  };

  const handleOnClose = () => {
    setTranslatePrefix(originalTranslatePrefix);
    setPreviews([]);
    setApiResponses([]);
    setIsCheckBoxClicked(false);
    setIsPreventCancelDialogOpened(false);
    setIsOpen(false);
  };

  const handlePreventCancel = () => {
    if (previews.length > 0) {
      setIsPreventCancelDialogOpened(true);
    } else {
      handleOnClose();
    }
  };

  return (
    <>
      <PreventCancelDialog
        isOpen={isPreventCancelDialogOpened}
        onConfirmClose={handleOnClose}
        onClose={() => {
          setIsPreventCancelDialogOpened(false);
        }}
      />
      <UploadingDialog
        isOpen={isUploadingDialogOpened}
        progress={apiResponses.length}
        totalRecords={numberOfValidPreviews}
        uploadingText={t('createRecordProgress', {
          progress: apiResponses.length,
          totalRecords: numberOfValidPreviews,
        })}
      />
      <ExportResultDialog
        isOpen={isExportResultDialogOpened}
        setIsOpen={setIsExportResultDialogOpened}
        validPreviews={validPreviews}
        apiResponses={apiResponses}
        clearData={handleOnClose}
      />
      <InvalidRecordReminderDialog
        isOpen={isInvalidRecordReminderDialogOpened}
        setIsOpen={setIsInvalidRecordReminderDialogOpened}
      />
      <DialogInOne
        title={t('batchAllocateCredit')}
        size="xl"
        self={{
          open: isOpen,
          onClose: handlePreventCancel,
        }}
        content={<Content previews={previews} setPreviews={setPreviews} />}
        isConfirmHidden
        isCancelHidden
        onConfirm={() => {}}
        onCancel={() => {}}
        actionButtons={
          <>
            {isSomeValidPreviews && (
              <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
                <Checkbox
                  checked={isCheckBoxClicked}
                  onClick={() => setIsCheckBoxClicked(!isCheckBoxClicked)}
                />
                <Typography variant={'body1'}>
                  {t('onlyCreateRecords', { passedRequests: numberOfValidPreviews })}
                </Typography>
              </Box>
            )}
            <DialogActions>
              <Button
                className={'inheritColor'}
                color={'primary'}
                variant={'contained'}
                children={tc('confirm')}
                onClick={onConfirm}
              />
              <Button
                className={'inheritColor'}
                color={'warning'}
                variant={'contained'}
                children={tc('cancel')}
                onClick={handlePreventCancel}
              />
            </DialogActions>
          </>
        }
      />
    </>
  );
};
export default BatchAllocateCreditDialog;
